import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import ChatAgent from "./ChatAgent";
import "./Agent.css";
import { openDocumentUploadBucket } from "../utils/documentUploadLink";

const DEFAULT_CHAT_API_URL = "https://fnol-frontend-api-650747488835.us-central1.run.app";

const getPublicChatApiUrl = () =>
  (process.env.REACT_APP_CHAT_API_URL || DEFAULT_CHAT_API_URL).replace(/\/$/, "");

const getLocalChatApiPorts = () => {
  const configuredPort = process.env.REACT_APP_CHAT_API_PORT;
  return [...new Set(["5001", configuredPort, "5000"].filter(Boolean))];
};

const getAgentListEndpoints = () => {
  const publicApiUrl = getPublicChatApiUrl();
  const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const shouldTryLocalApi =
    isLocalHost ||
    new URLSearchParams(window.location.search).get("localApi") === "1";
  const endpoints = [];

  if (shouldTryLocalApi) {
    getLocalChatApiPorts().forEach((apiPort) => {
      endpoints.push(`http://localhost:${apiPort}/api/chat/agents`);
    });
    if (isLocalHost) endpoints.push("/api/chat/agents");
  }

  if (publicApiUrl) endpoints.push(`${publicApiUrl}/api/chat/agents`);

  return [...new Set(endpoints)];
};

const fallbackAgents = [
  {
    id: "insurance-fnol-claims",
    name: "Insurance - FNOL & Claims Mgmt Agent",
    backendReady: true,
    missingConfigFields: [],
    apiPort: 5000,
  },
  {
    id: "insurance-fnol-claims-new",
    name: "Insurance -  FNOL & Claims Mgmt Agent-New Multilingual",
    backendReady: true,
    missingConfigFields: [],
    apiPort: 5000,
  },
  {
    id: "gmail-fnol-intake-agent",
    name: "Gmail FNOL Intake Agent",
    backendReady: true,
    missingConfigFields: [],
    apiPort: 5001,
  },
  {
    id: "fnol-intake-agent",
    name: "FNOL Intake Agent",
    backendReady: true,
    missingConfigFields: [],
    apiPort: 5001,
  },
  {
    id: "insurance-fnol-claims-assist",
    name: "Insurance - FNOL & Claims Mgmt - Agent Assist",
    backendReady: true,
    missingConfigFields: [],
    apiPort: 5000,
  },
];

const fnolModuleAgentId = "insurance-fnol-claims-new";

const mergeAgentsWithFallback = (loadedAgents = []) => {
  const agentsById = new Map();

  [...fallbackAgents, ...loadedAgents].forEach((agent) => {
    if (agent?.id) agentsById.set(agent.id, agent);
  });

  return Array.from(agentsById.values());
};

const getAgentStatusLabel = (agent) => {
  if (agent.backendReady) return "";

  const missingFields = agent.missingConfigFields || [];
  const hasAppConfigured =
    !missingFields.includes("projectId") &&
    !missingFields.includes("location") &&
    !missingFields.includes("appId");

  return hasAppConfigured ? "Deployment needed" : "Config needed";
};

const fetchWithTimeout = async (url, timeoutMs = 5000) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const Agent = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agents, setAgents] = useState(fallbackAgents);
  const [selectedAgentId, setSelectedAgentId] = useState(fallbackAgents[0].id);
  const isLoadingAgents = false;
  const [loadError, setLoadError] = useState("");
  const isFnolModule = new URLSearchParams(location.search).get("module") === "fnol";

  useEffect(() => {
    let isMounted = true;

    const loadAgents = async () => {
      setLoadError("");

      for (const endpoint of getAgentListEndpoints()) {
        try {
          const response = await fetchWithTimeout(endpoint);
          if (!response.ok) throw new Error("Unable to load agents");

          const data = await response.json();
          const loadedAgents = Array.isArray(data.agents) ? data.agents : [];
          const availableAgents = mergeAgentsWithFallback(loadedAgents);

          if (isMounted) {
            if (availableAgents.length) setAgents(availableAgents);
            setSelectedAgentId((currentId) =>
              currentId || availableAgents[0]?.id || fallbackAgents[0].id
            );
          }
          return;
        } catch (error) {
          // Keep the immediately-rendered fallback list and try the next endpoint.
        }
      }

      if (isMounted) {
        setSelectedAgentId((currentId) => currentId || fallbackAgents[0].id);
        setAgents(fallbackAgents);
        setLoadError("");
      }
    };

    loadAgents();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const agentId = new URLSearchParams(location.search).get("agentId");

    if (agentId && agents.some((agent) => agent.id === agentId)) {
      setSelectedAgentId(agentId);
      return;
    }

    if (
      isFnolModule &&
      agents.some((agent) => agent.id === fnolModuleAgentId)
    ) {
      setSelectedAgentId(fnolModuleAgentId);
    }
  }, [agents, isFnolModule, location.search]);

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) || agents[0],
    [agents, selectedAgentId]
  );

  useEffect(() => {
    if (!isFnolModule && !location.state?.fromFnolModule) {
      navigate("/home", { replace: true });
    }
  }, [isFnolModule, location.state, navigate]);

  return (
    <div className="agent-page">
      <Header onLogout={onLogout} />

      <div className="agent-page-body">
        <aside className="sidebar mini-sidebar">
          <nav className="sidebar-nav">
            {isFnolModule ? (
              <>
                <button onClick={() => navigate("/home")} className="nav-item">
                  <i className="fas fa-home"></i>
                  <span className="nav-label">Home</span>
                </button>
                <button onClick={() => navigate("/crm?module=fnol")} className="nav-item">
                  <i className="fas fa-address-book"></i>
                  <span className="nav-label">FNOL</span>
                </button>
                <button
                  onClick={() => navigate(`/ai-agents?module=fnol&agentId=${fnolModuleAgentId}`, { state: { fromFnolModule: true } })}
                  className="nav-item active"
                >
                  <i className="fas fa-robot"></i>
                  <span className="nav-label">AI Agents</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate("/home")} className="nav-item">
                  <i className="fas fa-home"></i>
                  <span className="nav-label">Home</span>
                </button>
                <button onClick={() => navigate("/my-work")} className="nav-item">
                  <i className="fas fa-briefcase"></i>
                  <span className="nav-label">My Work</span>
                </button>
                <button onClick={() => navigate("/crm")} className="nav-item">
                  <i className="fas fa-address-book"></i>
                  <span className="nav-label">FNOL</span>
                </button>
                <button onClick={() => navigate("/claims")} className="nav-item">
                  <i className="fas fa-file-shield"></i>
                  <span className="nav-label">Claim</span>
                </button>
                <button onClick={() => navigate("/ops-dashboard")} className="nav-item">
                  <i className="fas fa-tachometer-alt"></i>
                  <span className="nav-label">Ops Dashboard</span>
                </button>
                <button onClick={() => navigate("/claims?tab=quality-score")} className="nav-item quality-score-nav-item">
                  <i className="fas fa-clipboard-check"></i>
                  <span className="nav-label">QA</span>
                </button>
                <button
                  onClick={() => navigate("/ai-agents", { state: { fromFnolModule: false } })}
                  className="nav-item active"
                >
                  <i className="fas fa-gauge-high"></i>
                  <span className="nav-label">AI Agent Dashboard</span>
                </button>
                <button onClick={openDocumentUploadBucket} className="nav-item">
                  <i className="fas fa-file-arrow-up"></i>
                  <span className="nav-label">Document Upload</span>
                </button>
              </>
            )}
          </nav>
          <div className="sidebar-footer">
            <button onClick={onLogout} className="nav-item logout-btn">
              <i className="fas fa-sign-out-alt"></i>
              <span className="nav-label">Logout</span>
            </button>
          </div>
        </aside>

        <main className="agent-main" aria-label="AI Agents">
          <div className="agent-split-layout">
            <section className="agent-list-panel" aria-label="Available agents">
              <h2 className="section-title">Conversation Agents</h2>

              {loadError && <div className="agent-load-error">{loadError}</div>}

              <div className="agent-grid">
                {isLoadingAgents ? (
                  <div className="agent-card agent-card--static">
                    <span className="agent-name">Loading agents...</span>
                  </div>
                ) : (
                  agents.map((agent) => (
                    <button
                      className={`agent-card ${
                        selectedAgent?.id === agent.id ? "selected" : ""
                      }`}
                      key={agent.id}
                      onClick={() => setSelectedAgentId(agent.id)}
                      type="button"
                    >
                      <span className="agent-name">{agent.name}</span>
                      {!agent.backendReady && (
                        <span className="agent-config-note">
                          {getAgentStatusLabel(agent)}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </section>

            <div className="chat-panel-wrapper">
              <ChatAgent agent={selectedAgent} />
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default Agent;
